import React, { useState, useCallback } from 'react';
import { NumberInput, Group, Stack, Text } from '@mantine/core';
import { IconMapPin } from '@tabler/icons-react';
import { CoordinateInputProps, CoordinateValidation } from './CoordinateInput.types';

/**
 * CoordinateInput Component
 *
 * Input component for latitude and longitude coordinates
 *
 * Features:
 * - Separate inputs for latitude and longitude
 * - Validation for valid coordinate ranges
 * - Decimal precision control
 * - Required field validation
 * - Error display per field
 *
 * Validation Rules:
 * - Latitude: -90 to +90
 * - Longitude: -180 to +180
 * - Numeric values only
 *
 * Usage:
 * ```tsx
 * <CoordinateInput
 *   value={{ latitude: 40.7128, longitude: -74.0060 }}
 *   onChange={handleCoordinateChange}
 *   required
 * />
 * ```
 */
export const CoordinateInput: React.FC<CoordinateInputProps> = ({
  value,
  onChange,
  label = 'Coordinates',
  description = 'Enter latitude and longitude',
  latitudeError,
  longitudeError,
  required = false,
  disabled = false,
  precision = 6
}): React.ReactElement => {
  const [latError, setLatError] = useState<string | undefined>(latitudeError);
  const [lngError, setLngError] = useState<string | undefined>(longitudeError);

  /**
   * Validate latitude
   */
  const validateLatitude = useCallback((lat: number | string): CoordinateValidation => {
    const numLat = typeof lat === 'string' ? parseFloat(lat) : lat;

    if (isNaN(numLat)) {
      return { isValid: false, error: 'Invalid latitude value' };
    }

    if (numLat < -90 || numLat > 90) {
      return { isValid: false, error: 'Latitude must be between -90 and 90' };
    }

    return { isValid: true };
  }, []);

  /**
   * Validate longitude
   */
  const validateLongitude = useCallback((lng: number | string): CoordinateValidation => {
    const numLng = typeof lng === 'string' ? parseFloat(lng) : lng;

    if (isNaN(numLng)) {
      return { isValid: false, error: 'Invalid longitude value' };
    }

    if (numLng < -180 || numLng > 180) {
      return { isValid: false, error: 'Longitude must be between -180 and 180' };
    }

    return { isValid: true };
  }, []);

  /**
   * Handle latitude change
   */
  const handleLatitudeChange = useCallback(
    (newLat: string | number): void => {
      const numLat = typeof newLat === 'string' ? parseFloat(newLat) : newLat;

      // Validate
      const validation = validateLatitude(numLat);
      if (!validation.isValid) {
        setLatError(validation.error);
        return;
      }

      setLatError(undefined);
      onChange({
        latitude: numLat,
        longitude: value.longitude
      });
    },
    [value.longitude, onChange, validateLatitude]
  );

  /**
   * Handle longitude change
   */
  const handleLongitudeChange = useCallback(
    (newLng: string | number): void => {
      const numLng = typeof newLng === 'string' ? parseFloat(newLng) : newLng;

      // Validate
      const validation = validateLongitude(numLng);
      if (!validation.isValid) {
        setLngError(validation.error);
        return;
      }

      setLngError(undefined);
      onChange({
        latitude: value.latitude,
        longitude: numLng
      });
    },
    [value.latitude, onChange, validateLongitude]
  );

  return (
    <Stack gap="xs">
      {/* Label and Description */}
      {(label || description) && (
        <div>
          {label && (
            <Text size="sm" fw={500} mb={4}>
              {label}
              {required && (
                <Text component="span" c="red" ml={4}>
                  *
                </Text>
              )}
            </Text>
          )}
          {description && (
            <Text size="xs" c="dimmed">
              {description}
            </Text>
          )}
        </div>
      )}

      {/* Coordinate Inputs */}
      <Group grow align="flex-start">
        <NumberInput
          label="Latitude"
          placeholder="40.7128"
          value={value.latitude}
          onChange={handleLatitudeChange}
          min={-90}
          max={90}
          decimalScale={precision}
          fixedDecimalScale={false}
          step={0.000001}
          stepHoldDelay={500}
          stepHoldInterval={100}
          required={required}
          disabled={disabled}
          error={latError || latitudeError}
          leftSection={<IconMapPin size={16} />}
          description="Range: -90 to 90"
        />

        <NumberInput
          label="Longitude"
          placeholder="-74.0060"
          value={value.longitude}
          onChange={handleLongitudeChange}
          min={-180}
          max={180}
          decimalScale={precision}
          fixedDecimalScale={false}
          step={0.000001}
          stepHoldDelay={500}
          stepHoldInterval={100}
          required={required}
          disabled={disabled}
          error={lngError || longitudeError}
          leftSection={<IconMapPin size={16} />}
          description="Range: -180 to 180"
        />
      </Group>

      {/* Current coordinates display */}
      <Text size="xs" c="dimmed" ff="monospace">
        Current: {value.latitude.toFixed(precision)}, {value.longitude.toFixed(precision)}
      </Text>
    </Stack>
  );
};
